import {
  faComment,
  faExchangeAlt,
  faExclamationCircle,
  faExternalLinkAlt,
  faFileInvoice,
  faInfoCircle,
  faRandom,
  faUndoAlt,
  faVideo,
  faWarning,
} from '@fortawesome/free-solid-svg-icons';

import {
  IPurchasedProduct,
  IProductAction,
  SubscriptionStatus,
} from '~/types/account';

export enum ACCOUNT_PAGES {
  PROFILE = 'profile',
  PRODUCTS = 'products',
  PAYMENTS = 'payments',
  CREDITS = 'credits',
  PLUS = 'plus',
}

export enum PLUS_SUBSCRIPTION_STATUS {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  INACTIVE = 'inactive',
}

export enum PRODUCTS_STATUS {
  TRANSFERRED = 'transferred',
  REDEEMED = 'redeemed',
  UNREDEEMED = 'unredeemed',
  OUTDATED = 'outdated',
  REFUNDED = 'refunded',
  ACTIVATED = 'activated',
  INACTIVE = 'inactive',
  EXPIRED = 'expired',
}

export enum PRODUCT_ACTIONS {
  CHANGE_PLAN = 'change-plan',
  LEAVE_REVIEW = 'leave-review',
  PRODUCT_PAGE = 'product-page',
  REDEEM = 'redeem',
  REDEMPTION = 'redemption',
  REFUND = 'refund',
  TRANSFER = 'transfer',
  VIEW_INVOICE = 'view-invoice',
  WATCH_WEBINAR = 'watch-webinar',
  VIEW_MEMBERSHIP = 'view-membership',
}

export enum ACCOUNT_PRODUCT_FILTERS {
  ACTIVE = 'active',
  NOT_REDEEMED = 'not-redeemed',
  OUTDATED = 'outdated',
  REFUNDED = 'refunded',
  TRANSFERRED = 'transferred',
}

export enum SOCIAL_ACCOUNT_TYPES {
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  LINKEDIN = 'linkedin',
  TWITTER = 'twitter',
  YOUTUBE = 'youtube',
  TIKTOK = 'tiktok',
}

export const FILTER_OPTIONS = [
  { value: ACCOUNT_PRODUCT_FILTERS.ACTIVE, label: 'Active' },
  { value: ACCOUNT_PRODUCT_FILTERS.NOT_REDEEMED, label: 'Not redeemed' },
  { value: ACCOUNT_PRODUCT_FILTERS.OUTDATED, label: 'Outdated' },
  { value: ACCOUNT_PRODUCT_FILTERS.REFUNDED, label: 'Refunded' },
  // { value: 'transferred', label: 'Transferred' }, TBD on this one
];

export const PRODUCTS_ITEMS_PER_PAGE = 20;
export const PRODUCTS_FILTER_OPTIONS = [
  { value: 'active', display: 'Active' },
  { value: 'not-redeemed', display: 'Not Redeemed' },
  { value: 'outdated', display: 'Outdated' },
  { value: 'refunded', display: 'Refunded' },
];

export enum TAX_TYPE {
  ABN = 'abn',
  DEFAULT = 'default',
}

export enum EMAIL_UPDATE_TYPE {
  VERIFY = 'action_send',
  ADD = 'action_add',
  REMOVE = 'action_remove',
  PRIMARY = 'action_primary',
}

export const ACCOUNT_DATA_URL = '/api/users/account-data';
export const PROFILE_DATA_URL = '/api/account/profile';
export const ACCOUNT_UPDATE_URL = '/api/account/update/';
export const PLUS_API_URL = '/api/account/plus/';
export const PLUS_API_UPDATE_URL = '/api/account/plus/update/';
export const PRODUCTS_API = '/api/v2/account/products/';
export const PRODUCTS_DOWNLOAD_API = '/api/v2/account/products/download/';
export const USER_CREDITS_URL = '/api/users/account-credits/';
export const ACCOUNT_PM_UPDATE_URL = '/account/cards/update-default/';
export const ACCOUNT_PM_DELETE_URL = '/account/cards/delete/';
export const ACCOUNT_REDEEM_GIFT_URL = '/account/giftcard/redeem/';
export const RESET_PASSWORD_URL = '/account/password/';
export const TRANSFER_API_URL = '/license/activate/';
export const PUBLIC_PROFILE_API_URL = '/api/user/profile/';
export const ACCOUNT_PROFILE_URL = '/account/profile/';

export const ACCOUNT_ACTIONS_DEFAULT: Record<string, IProductAction> = {
  [PRODUCT_ACTIONS.REDEEM]: {
    label: 'Redemption information',
    value: PRODUCT_ACTIONS.REDEEM,
    img: faInfoCircle,
    disabled: false,
    url: (product: IPurchasedProduct) => `/account/redemption/${product.uuid}/`,
  },
  [PRODUCT_ACTIONS.LEAVE_REVIEW]: {
    label: 'Leave a review',
    value: PRODUCT_ACTIONS.LEAVE_REVIEW,
    img: faComment,
    disabled: false,
    url: (product: IPurchasedProduct) => `/products/${product.slug}/#reviews`,
  },
  [PRODUCT_ACTIONS.VIEW_INVOICE]: {
    label: 'View invoice',
    value: PRODUCT_ACTIONS.VIEW_INVOICE,
    img: faFileInvoice,
    disabled: false,
    url: (product: IPurchasedProduct) =>
      `/account/history/${product.invoice_uuid}/`,
  },
  [PRODUCT_ACTIONS.CHANGE_PLAN]: {
    label: 'Change plan',
    value: PRODUCT_ACTIONS.CHANGE_PLAN,
    img: faExchangeAlt,
    disabled: false,
    url: (product: IPurchasedProduct) =>
      `/account/redemption/${product.uuid}/change-plan/`,
  },
  [PRODUCT_ACTIONS.TRANSFER]: {
    label: 'Transfer Product',
    value: PRODUCT_ACTIONS.TRANSFER,
    img: faRandom,
    disabled: false,
  },
  [PRODUCT_ACTIONS.REFUND]: {
    label: 'Refund',
    value: PRODUCT_ACTIONS.REFUND,
    img: faUndoAlt,
    disabled: false,
    url: (product: IPurchasedProduct) =>
      `/account/redemption/${product.uuid}/#refund-instructions`,
  },
  [PRODUCT_ACTIONS.PRODUCT_PAGE]: {
    label: 'Product details',
    value: PRODUCT_ACTIONS.PRODUCT_PAGE,
    img: faExternalLinkAlt,
    disabled: false,
    url: (product: IPurchasedProduct) => `/products/${product.slug}/`,
  },
  [PRODUCT_ACTIONS.WATCH_WEBINAR]: {
    label: 'Watch webinar',
    value: PRODUCT_ACTIONS.WATCH_WEBINAR,
    img: faVideo,
    disabled: false,
    url: (product: IPurchasedProduct) => product.webinar.url ?? '',
    target: '_blank',
  },
  [PRODUCT_ACTIONS.VIEW_MEMBERSHIP]: {
    label: 'View Membership',
    value: PRODUCT_ACTIONS.VIEW_MEMBERSHIP,
    img: faVideo,
    disabled: false,
    url: () => '/account/plus/',
  },
};

export const ACCOUNT_PLUS_ALERTS: Record<SubscriptionStatus | string, any> = {
  [PLUS_SUBSCRIPTION_STATUS.CANCELED]: {
    bg: 'bg-lemon-light',
    text: (date: string) =>
      `You have canceled your membership. Access to your benefits will end on ${date}.`,
    icon: {
      definition: faWarning,
      color: 'text-black-pearl',
    },
  },
  // [PLUS_SUBSCRIPTION_STATUS.EXPIRED]: {
  //   bg: 'bg-lemon-light',
  //   text: 'Your renewal could not be processed. Please check your payment method and renew your membership to continue using your benefits.',
  //   icon: {
  //     definition: faWarning,
  //     color: 'text-black-pearl',
  //   },
  // },
  [PLUS_SUBSCRIPTION_STATUS.INACTIVE]: {
    bg: 'bg-brick-60',
    text: 'Your membership is inactive. Renew your membership to continue using your benefits!',
    icon: {
      definition: faExclamationCircle,
      color: 'text-brick',
    },
  },
};

export const ACCOUNT_SOCIAL_BASE_URLS = {
  [SOCIAL_ACCOUNT_TYPES.FACEBOOK]: 'https://www.facebook.com/',
  [SOCIAL_ACCOUNT_TYPES.INSTAGRAM]: 'https://www.instagram.com/',
  [SOCIAL_ACCOUNT_TYPES.LINKEDIN]: 'https://www.linkedin.com/in/',
  [SOCIAL_ACCOUNT_TYPES.TWITTER]: 'https://twitter.com/',
  [SOCIAL_ACCOUNT_TYPES.YOUTUBE]: 'https://www.youtube.com/@',
  [SOCIAL_ACCOUNT_TYPES.TIKTOK]: 'https://www.tiktok.com/@',
};
